import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@comps/ui/dialog';
import { TW_AppDialogButton } from '@utils/twClasses';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { textToHTML } from 'src/i18n/i18n';
import { useUserStore } from 'src/store';
import { langToContentKey } from './announcementsLogic';
import { Announcement } from './content/announcements';

interface AnnouncementsDialogProps {
    open: boolean;
    announcements: Announcement[]; // already filtered and sorted by the caller
    onClose: () => void;           // every close path (X, ESC, overlay, button) goes through here
}

function AnnouncementsDialog({ open, announcements, onClose }: AnnouncementsDialogProps) {
    const { t } = useTranslation();
    const lang = useUserStore(state => state.lang);
    const contentLang = langToContentKey(lang);
    const dateLocale = contentLang === 'fr' ? 'fr-FR' : 'en-US';

    if (announcements.length === 0) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{t('announcements.dialog.title.text')}</DialogTitle>
                    <DialogDescription>{t('announcements.dialog.subtitle.text')}</DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto flex flex-col gap-4">
                    {announcements.map((announcement, index) => (
                        <div
                            key={announcement.id}
                            className={index > 0 ? 'border-t pt-4 flex flex-col gap-1' : 'flex flex-col gap-1'}
                        >
                            <h3 className="font-semibold">{announcement.title[contentLang]}</h3>
                            <span className="text-xs text-muted-foreground">
                                {new Date(announcement.date + 'T00:00:00').toLocaleDateString(dateLocale)}
                            </span>
                            <div className="text-sm flex flex-col gap-2">
                                {textToHTML(announcement.body[contentLang])}
                            </div>
                            {announcement.link?.manualSlug && (
                                <Link
                                    to="/manual"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm underline"
                                >
                                    {t('announcements.dialog.readMore.link')}
                                </Link>
                            )}
                            {!announcement.link?.manualSlug && announcement.link?.url && (
                                <a
                                    href={announcement.link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm underline"
                                >
                                    {t('announcements.dialog.readMore.link')}
                                </a>
                            )}
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <button className={TW_AppDialogButton} onClick={onClose}>
                        {t('announcements.dialog.close.button')}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AnnouncementsDialog;
